import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Neural-network style preloader.
 * - Always shows on initial mount.
 * - Hides as soon as window 'load' fires OR after MAX_MS, whichever is first.
 * - Minimum visible time keeps the animation from being jarring (MIN_MS).
 */
const MAX_MS = 8000; // hard cap
const MIN_MS = 8000; // minimum on-screen time so the neural net animation completes

// Build a simple feed-forward network: 3 layers
const LAYERS = [4, 5, 3];
const WIDTH = 520;
const HEIGHT = 340;
const PADDING_X = 70;
const PADDING_Y = 50;

const buildGraph = () => {
  const xs = LAYERS.map((_, i) =>
    PADDING_X + (i * (WIDTH - 2 * PADDING_X)) / (LAYERS.length - 1)
  );
  const nodes = [];
  LAYERS.forEach((count, li) => {
    const ys = Array.from({ length: count }, (_, ni) => {
      const step = (HEIGHT - 2 * PADDING_Y) / Math.max(1, count - 1);
      return PADDING_Y + ni * step;
    });
    ys.forEach((y, ni) => nodes.push({ id: `${li}-${ni}`, layer: li, x: xs[li], y }));
  });
  const edges = [];
  for (let li = 0; li < LAYERS.length - 1; li += 1) {
    const a = nodes.filter((n) => n.layer === li);
    const b = nodes.filter((n) => n.layer === li + 1);
    a.forEach((from) => {
      b.forEach((to) => edges.push({ id: `${from.id}->${to.id}`, from, to }));
    });
  }
  return { nodes, edges };
};

const GRAPH = buildGraph();

export const Loader = () => {
  const [visible, setVisible] = useState(true);
  const startRef = useRef(0);
  const { nodes, edges } = GRAPH;

  useEffect(() => {
    startRef.current = performance.now();

    const finish = () => {
      const elapsed = performance.now() - startRef.current;
      const wait = Math.max(0, MIN_MS - elapsed);
      setTimeout(() => setVisible(false), wait);
    };

    // Hide when window says it's loaded
    if (document.readyState === "complete") {
      finish();
    } else {
      window.addEventListener("load", finish, { once: true });
    }

    // Hard cap
    const cap = setTimeout(() => setVisible(false), MAX_MS);

    // Lock scroll while loading
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("load", finish);
      clearTimeout(cap);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Unlock scroll once hidden
  useEffect(() => {
    if (!visible) {
      document.body.style.overflow = "";
    }
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="page-loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }}
          className="fixed inset-0 z-[100] bg-[#050505] flex flex-col items-center justify-center"
        >
          {/* faint grid */}
          <div className="absolute inset-0 grid-line opacity-40 pointer-events-none" />

          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio="xMidYMid meet"
            className="relative w-[min(80vw,520px)] h-auto"
            aria-hidden
          >
            {/* Edges */}
            {edges.map((e, i) => (
              <g key={e.id}>
                <motion.line
                  x1={e.from.x}
                  y1={e.from.y}
                  x2={e.to.x}
                  y2={e.to.y}
                  stroke="rgba(255,255,255,0.12)"
                  strokeWidth={1}
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.7, delay: 0.15 + i * 0.015 }}
                />
                {/* Traveling data pulse along the edge */}
                <motion.circle
                  r={2.5}
                  fill="#ffffff"
                  initial={{ cx: e.from.x, cy: e.from.y, opacity: 0 }}
                  animate={{
                    cx: [e.from.x, e.to.x],
                    cy: [e.from.y, e.to.y],
                    opacity: [0, 1, 0.6, 0],
                  }}
                  transition={{
                    duration: 1.4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.6 + (i % 7) * 0.18,
                    times: [0, 0.15, 0.85, 1],
                  }}
                />
              </g>
            ))}

            {/* Nodes */}
            {nodes.map((n, i) => (
              <g key={n.id}>
                {/* Outer pulse */}
                <motion.circle
                  cx={n.x}
                  cy={n.y}
                  r={6}
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth={1}
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: [0.8, 2.1, 0.8], opacity: [0.6, 0, 0.6] }}
                  transition={{
                    duration: 2.2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.4 + (i % 4) * 0.25,
                  }}
                  style={{ transformOrigin: `${n.x}px ${n.y}px` }}
                />
                {/* Core node */}
                <motion.circle
                  cx={n.x}
                  cy={n.y}
                  r={5}
                  fill="#ffffff"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.45, delay: 0.2 + i * 0.05 }}
                  style={{ transformOrigin: `${n.x}px ${n.y}px` }}
                />
              </g>
            ))}
          </svg>

          {/* Caption */}
          <div className="relative mt-10 flex flex-col items-center gap-3">
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="font-display font-black text-2xl tracking-tighter text-white"
            >
              SATYAM<span className="text-white/30">.</span>
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-white/50 flex items-center gap-3"
            >
              <span>Initialising neural net</span>
              <motion.span
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.2, repeat: Infinity }}
              >
                ●
              </motion.span>
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Loader;
